<template>
	<div class="template-preview" :class="wrapperClasses">
		<div class="tw-flex tw-justify-between">
			<p class="tw-font-semibold tw-text-gray-700" v-if="showTitle">
				{{ $t('templates.createEdit.preview.templatePreview') }}
			</p>
			<slot name="close-button"></slot>
		</div>
		<p class="tw-mb-4 tw-font-normal tw-text-gray-600" v-if="showSubTitle">
			{{ $t('templates.createEdit.preview.templateMessage') }}
		</p>

		<div
			class="preview-wrapper"
			:style="
				(bgColor ? `background-color:${bgColor};` : `background-image: url(${bgImage});`) +
				`min-height:${minHeight}`
			"
		>
			<div class="content-wrapper">
				<div class="preview-header" dir="auto" v-if="form?.header?.format">
					<span
						class="form-text break-word"
						v-if="form?.header?.format === TemplateHeaderFormats.TEXT"
						>{{ form?.header?.text }}</span
					>
					<img
						v-if="form?.header?.format === TemplateHeaderFormats.IMAGE"
						class="w-100 tw-rounded"
						:src="
							form?.header?.image_url
								? form?.header?.image_url
								: loadFile('assets/v2/image/whatsapp/defualt-img-preview.webp')
						"
						:class="{
							'tw-rounded-b-none': type === TemplateTypes.LTO,
							'tw-mb-3': type !== TemplateTypes.LTO,
						}"
						style="max-height: 250px"
					/>
					<video
						v-if="form?.header?.format === TemplateHeaderFormats.VIDEO"
						class="w-100 tw-rounded"
						controls
						:src="form?.header?.video_url"
						:class="{
							'tw-rounded-b-none': type === TemplateTypes.LTO,
							'tw-mb-3': type !== TemplateTypes.LTO,
						}"
						style="max-height: 250px"
					></video>
					<a
						v-if="form?.header?.format === TemplateHeaderFormats.DOCUMENT"
						:href="form?.header?.document?.link || form?.header?.document_url"
						:download="documentFileName"
						target="_blank"
						rel="noopener noreferrer"
						class="w-100 tw-rounded tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-bg-white tw-p-2 tw-no-underline tw-text-inherit hover:tw-bg-gray-50"
						style="border: 1px solid #e5e7eb"
					>
						<InlineSvg :src="loadFile('assets/v2/image/icons/file.svg')" width="20px" height="20px" />
						<span class="tw-truncate tw-text-sm">{{ documentFileName }}</span>
					</a>
					<div v-if="form?.header?.format === TemplateHeaderFormats.LOCATION" class="w-100 tw-rounded tw-mb-3 tw-bg-white tw-p-3" style="border: 1px solid #e5e7eb">
						<p class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">
							📍{{ form?.header?.location?.name || 'الموقع' }}
						</p>
						<p class="tw-text-sm tw-text-gray-700" v-if="form?.header?.location?.address">
							{{ form?.header?.location?.address }}
						</p>
						<p class="tw-text-xs tw-text-gray-500" v-if="form?.header?.location?.latitude && form?.header?.location?.longitude">
							{{ form?.header?.location?.latitude }}, {{ form?.header?.location?.longitude }}
						</p>
					</div>
				</div>
				<div
					v-if="type === TemplateTypes.LTO"
					class="tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-rounded-b tw-px-2 tw-py-4"
					style="background: #dbc3a7"
					:class="{ 'tw-rounded-t': form?.header?.format !== TemplateHeaderFormats.IMAGE }"
				>
					<div
						class="tw-flex tw-h-[44px] tw-w-[44px] tw-items-center tw-justify-center tw-rounded-full"
						style="background: #ae927287"
					>
						<InlineSvg
							:src="loadFile('assets/v2/image/icons/gift.svg')"
							width="17px"
							height="17px"
							style="color: #94724b"
						/>
					</div>
					<div class="">
						<p class="tw-text-sm tw-font-bold tw-text-gray-950">{{ form?.lto?.text }}</p>
						<p
							class="tw-text-sm tw-font-medium tw-text-gray-700"
							v-if="form?.lto?.has_expiration && form?.lto?.expiration_time_ms"
						>
							{{ $t('templates.createEdit.preview.offer-ends') }}
							{{ moment(parseInt(form?.lto?.expiration_time_ms)).format('MMMM DD') }}
						</p>
						<p v-if="form?.lto?.coupon_code" class="tw-text-sm tw-text-gray-700">
							{{ $t('templates.createEdit.preview.code') }} {{ form?.lto?.coupon_code }}
						</p>
					</div>
				</div>
				<p
					class="preview-body break-word"
					dir="auto"
					v-if="form?.body?.text"
					v-html="htmlData"
				></p>
				<p class="preview-footer tw-text-gray-500" dir="auto" v-if="form?.footer?.text">
					{{ form?.footer?.text }}
				</p>
			</div>

			<div class="preview-link">
				<!--				<template v-if="type === TemplateTypes.CUSTOM || type === TemplateTypes.LTO">-->

				<!--				</template>-->

				<template v-for="item in form.buttons.buttons">
					<p
						class="link-item tw-truncate"
						v-if="
							item &&
							(!Object.keys(item).includes('toggle') ||
								(Object.keys(item).includes('toggle') && item.toggle))
						"
					>
						{{ item?.text }}
					</p>
				</template>
				<template v-if="!form?.template?.id">
					<p v-if="linkName" class="link-item tw-truncate">
						{{ linkName }}
					</p>

					<p
						v-if="(type === TemplateTypes.CUSTOM || type === TemplateTypes.LTO) && form?.category !== 'UTILITY'"
						class="link-item tw-truncate"
					>
						{{ $t('templates.createEdit.opt-out') }}
					</p>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup>
import {
	TemplateHeaderFormats,
	TemplateTypes,
} from '@/v2/modules/whatsapp/entities/templates/interfaces.js';
import { loadFile } from '@/v2/utils/index';
import { useMarkdownConverter } from '@/v2/utils/markdown';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import InlineSvg from '@/v2/components/inline-svg';
import moment from 'moment';

const props = defineProps({
	type: String,
	form: {},
	wrapperClasses: {
		type: String,
		default: 'border-1 tw-rounded-[8px] tw-border-gray-300 tw-p-5',
	},
	bgImage: {
		type: String,
		default: 'assets/v2/image/whatsapp/whatsapp-background.webp',
	},
	bgColor: {
		type: String,
		default: null,
	},
	showTitle: {
		type: Boolean,
		default: true,
	},
	showSubTitle: {
		type: Boolean,
		default: true,
	},
	minHeight: {
		type: String,
		default: '500px',
	},
});
const { t } = useI18n();

const htmlData = computed(() => {
	const variables = props.form?.body?.example?.body_text?.length
		? props.form.body?.example?.body_text[0]
		: [];
	const variablesIndices = {};
	variables.forEach((variable, i) => {
		variablesIndices[variable] = i + 1;
	});
	const { markdownToHtml } = useMarkdownConverter({ variablesIndices });

	return markdownToHtml(props.form?.body?.text);
});

const documentFileName = computed(() => {
	const url = props.form?.header?.document_url;
	return url ? url.split('/').pop() : 'document.pdf';
});

const linkName = computed(() => {
	switch (props.type) {
		case TemplateTypes.CATALOG: {
			return t('templates.createEdit.preview.viewCatalog');
			break;
		}
		case TemplateTypes.MULTI_PRODUCT: {
			return t('templates.createEdit.preview.viewItems');
			break;
		}
		case TemplateTypes.LTO: {
			return t('templates.createEdit.preview.copyOfferCode');
			break;
		}
		default: {
			return null;
			break;
		}
	}
});
</script>

<style lang="scss" scoped>
.template-preview {
	.preview-wrapper {
		background-size: contain;
		border-radius: 8px;
		padding: 16px;
		.content-wrapper {
			padding: 8px;
			background: #fff;
			border-radius: 4px;
			margin-bottom: 10px;
			min-height: 30px;
			.preview-header {
				.form-text {
					font-size: 16px;
					font-weight: 700;
					margin-bottom: 5px;
					line-height: 1;
					color: black;
				}
			}
			.preview-body {
				font-size: 16px;
				font-weight: 400;
				margin-bottom: 5px;
				line-height: 1;
				color: black;
			}
			.preview-footer {
				font-size: 14px;
				font-weight: 400;
				margin-bottom: 0;
				line-height: 1;
			}
		}

		.preview-link {
			.link-item {
				font-size: 16px;
				color: rgba(0, 150, 222, 1);
				font-weight: 400;
				padding: 8px;
				border-radius: 4px;
				margin-bottom: 4px;
				text-align: center;
				display: block;
				width: 100%;
				background-color: #fff;
			}
		}
	}
}

.break-word {
	word-break: break-word;
}
</style>
